import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export type PollType   = 'circuit' | 'car' | 'rule' | 'points' | 'schedule' | 'team_format' | 'generic';
export type PollMode   = 'single' | 'multi' | 'ranked';
export type PollStatus = 'draft' | 'open' | 'closed';
export type PollEligibility = 'all' | 'active_players';

export interface PollOptionItem {
    option_id?:  number;
    poll_id?:    number;
    label:       string;
    ref_type?:   string | null;
    ref_id?:     number | null;
    sort_order?: number;
}

export interface PollItem {
    poll_id?:          number;
    season_id:         number;
    title:             string;
    description?:      string | null;
    type:              PollType;
    mode:              PollMode;
    max_selections?:   number;
    status:            PollStatus;
    eligibility:       PollEligibility;
    opens_at?:         string | null;
    duration_days?:    number | null;
    closes_at?:        string | null;
    created_by?:       number | null;
    created_at?:       string | null;
    options?:          PollOptionItem[];
    effective_status?: PollStatus;
    vote_count?:       number;
}

export interface BallotSelection {
    option_id: number;
    rank?:     number;
}

export interface MyVoteResponse {
    poll_id:          number;
    ballot:           { option_id: number; rank: number }[];
    eligible:         boolean;
    effective_status: PollStatus;
}

export interface PollResultRow {
    option_id: number;
    label:     string;
    ref_type:  string | null;
    ref_id:    number | null;
    score:     number;
    votes:     number;
}

export interface PollResults {
    poll_id:          number;
    season_id?:       number;
    title?:           string;
    closes_at?:       string | null;
    mode:             PollMode;
    type:             PollType;
    max_selections:   number;
    effective_status: PollStatus;
    voter_count:      number;
    results:          PollResultRow[];
}

export interface PollVoteRow {
    vote_id:      number;
    user_id:      number;
    user_name:    string;
    option_id:    number;
    option_label: string;
    rank:         number;
    created_at:   string | null;
}

export interface ActivePoll {
    poll_id:   number;
    season_id: number;
    title:     string;
    type:      PollType;
    mode:      PollMode;
    status:    PollStatus;
    opens_at?: string | null;
    closes_at?: string | null;
}

/** How long one pollsActive.php answer serves all callers (menu, guard, dashboard). */
const ACTIVE_POLLS_TTL_MS = 30_000;

// Root-provided so the sidebar menu, the dashboard announcement and the voting
// guard share ONE instance (and thus one cached getActivePolls request) —
// per-component `providers: [PollsService]` would defeat that.
@Injectable({ providedIn: 'root' })
export class PollsService {
    private base       = environment.apiBaseUrl;
    private crudUrl    = `${this.base}/api/pollsCrud.php`;
    private voteUrl    = `${this.base}/api/pollVote.php`;
    private activeUrl  = `${this.base}/api/pollsActive.php`;
    private resultsUrl = `${this.base}/api/pollResults.php`;
    private votesUrl   = `${this.base}/api/pollVotesAdmin.php`;

    private activePolls$: Observable<ActivePoll[]> | null = null;
    private activeFetchedAt = 0;

    constructor(private http: HttpClient) {}

    getPolls(season_id: number): Observable<PollItem[]> {
        return this.http.get<PollItem[]>(`${this.crudUrl}?season_id=${season_id}`);
    }

    /**
     * Active polls, cached for 30 s: menu, guard and dashboard announcement all
     * ask within one navigation — one request serves them all. An error empties
     * the cache so the next caller retries.
     */
    getActivePolls(): Observable<ActivePoll[]> {
        const now = Date.now();
        if (!this.activePolls$ || now - this.activeFetchedAt > ACTIVE_POLLS_TTL_MS) {
            this.activeFetchedAt = now;
            this.activePolls$ = this.http.get<ActivePoll[]>(this.activeUrl).pipe(
                catchError((err) => {
                    this.activePolls$ = null;
                    return throwError(() => err);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        return this.activePolls$;
    }

    savePoll(poll: PollItem): Observable<any> {
        return poll.poll_id
            ? this.http.put<any>(this.crudUrl, poll)
            : this.http.post<any>(this.crudUrl, poll);
    }

    deletePoll(poll_id: number): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(this.crudUrl, { body: { poll_id } });
    }

    getMyVote(poll_id: number): Observable<MyVoteResponse> {
        return this.http.get<MyVoteResponse>(`${this.voteUrl}?poll_id=${poll_id}`);
    }

    castVote(poll_id: number, selections: BallotSelection[]): Observable<{ success: boolean; count: number }> {
        return this.http.post<{ success: boolean; count: number }>(this.voteUrl, { poll_id, selections });
    }

    getResults(poll_id: number): Observable<PollResults> {
        return this.http.get<PollResults>(`${this.resultsUrl}?poll_id=${poll_id}`);
    }

    /** Admin-only: raw cast ballots for a poll, with voter names. */
    getVotes(poll_id: number): Observable<PollVoteRow[]> {
        return this.http.get<PollVoteRow[]>(`${this.votesUrl}?poll_id=${poll_id}`);
    }

    /** Admin-only: drop one voter's whole ballot for a poll (they may vote again while it is open). */
    deleteVoterBallot(poll_id: number, user_id: number): Observable<{ success: boolean; deleted: number }> {
        return this.http.delete<{ success: boolean; deleted: number }>(this.votesUrl, { body: { poll_id, user_id } });
    }

    /** Admin-only: drop every ballot of a poll; the poll and its options stay. */
    deleteAllVotes(poll_id: number): Observable<{ success: boolean; deleted: number }> {
        return this.http.delete<{ success: boolean; deleted: number }>(this.votesUrl, { body: { poll_id, all: true } });
    }
}
